import { useId, type ChangeEvent } from 'react';
import { Text } from '../Text/Text';
import styles from './Toggle.module.css';

/**
 * Figma: `Toggle` (324:12211) and `_Toggle base` — an on/off switch with an
 * optional label and supporting text.
 *
 * The wrapper set spans six properties, but four of them collapse: `Type` and
 * `Size` have a single option each, `Text` is simply whether a label was passed,
 * and `Supporting text` whether a second line was. `Pressed` and `Disabled` are
 * real input state; hover is CSS. `state` exists only to force hover for
 * documentation, the same escape hatch `Checkbox` uses.
 *
 * `_Toggle base` carries Figma's `_` internal marker and is not a separate
 * component here — it is one 36x20 track, and splitting it would buy nothing
 * that a class does not.
 */
export type ToggleState = 'default' | 'hover';

export type ToggleProps = {
  /** Figma `Pressed`. */
  checked?: boolean;
  disabled?: boolean;
  /** Primary label. Omit for a bare switch — Figma's `Text=False` variants. */
  label?: string;
  /** Second line under the label. Requires `label`. */
  supportingText?: string;
  name?: string;
  value?: string;
  onChange?: (checked: boolean) => void;
  /**
   * Accessible name for a bare switch. Required when there is no `label`,
   * because a lone track names nothing. Not in Figma.
   */
  'aria-label'?: string;
  /** Force the hover appearance for documentation and visual regression only. */
  state?: ToggleState;
  id?: string;
  className?: string;
};

export function Toggle({
  checked = false,
  disabled = false,
  label,
  supportingText,
  name,
  value,
  onChange,
  'aria-label': ariaLabel,
  state,
  id,
  className,
}: ToggleProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const trackClasses = [styles.track, state === 'hover' && !disabled ? styles.forceHover : null]
    .filter(Boolean)
    .join(' ');

  const rootClasses = [
    styles.root,
    label === undefined ? styles.bare : null,
    /* One line of text centres on the track; two lines hang from its top. See the CSS. */
    label !== undefined && supportingText === undefined ? styles.singleLine : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={rootClasses} htmlFor={inputId}>
      <span className={styles.inputWrapper}>
        {/*
         * A real checkbox with `role="switch"`. Not in Figma — Figma draws a
         * track, not a control — but it is what gives keyboard operation, form
         * participation and an on/off announcement for free.
         */}
        <input
          className={styles.input}
          id={inputId}
          type="checkbox"
          role="switch"
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          aria-label={label === undefined ? ariaLabel : undefined}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange?.(event.target.checked)}
        />
        <span className={trackClasses} aria-hidden>
          <span className={styles.knob} />
        </span>
      </span>

      {label !== undefined && (
        <span className={styles.textBlock}>
          {/*
           * Figma keeps the label at text/primary and the supporting line at
           * text/tertiary in every state, disabled included — only the track
           * changes. Matched here for the same reason Checkbox does.
           */}
          <Text size="sm" leading="none" weight="regular" color="primary">
            {label}
          </Text>
          {supportingText !== undefined && (
            <Text size="sm" leading="none" weight="regular" color="tertiary">
              {supportingText}
            </Text>
          )}
        </span>
      )}
    </label>
  );
}
